import { FC } from "react";
import { explode, formatNumber } from "src/utility";
import { useTranslation } from "react-i18next";
import { useJourneyStaticDetails } from "../../../../context";
import { useSSRContext } from "../../context/SSRContext";
import { generateBaggageOptions, getBaggageLabel } from "../../utils";
import { BaggageOption } from "src/Modules/Flights/constants/JourneyDetails";
import classNames from "classnames";
import { Button, Empty, Grid } from "antd";
import { CheckSuccessIcon } from "src/assets/Icons";
import { Tag } from "antd";
import { PassengerSelectionTile } from "../PassengerSelection/components";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import { track } from "@amplitude/analytics-browser";

const BaggageItem: FC<{ baggage: BaggageOption; onChange: any }> = ({ baggage, onChange }) => {
  const { t } = useTranslation();
  const { getSSRItem, cursor } = useSSRContext();
  const baggageValues = getSSRItem("baggages", cursor.passengerId) as Record<string, BaggageOption>;
  const value = baggageValues[baggage.code];
  const selected = value && value.code === baggage.code;
  const handleClick = () => {
    if (selected) {
      track("Baggage Unselected",{
        ...baggage
      })
      return onChange({});
    }
    track("Baggage Selected",{
      ...baggage
    })
    onChange(baggage);
  };

  const priceTag =
    baggage.code === "NoBaggage" ? null : baggage?.price != 0 ? (
      `${formatNumber(baggage.price)} ${baggage.currency}`
    ) : (
      <Tag
        color="var(--color-background-success)"
        className="py-1.5! px-6! font-bold! rounded-sm! border-current! block! w-full! text-center! md:w-auto! md:inline-block! text-font-success!"
      >
        {t("Flights.details.ssr.free")}
      </Tag>
    );
  return (
    <div
      onClick={handleClick}
      className={classNames(
        "border-border-minimal group flex h-full w-full min-w-min cursor-pointer flex-col justify-between gap-10 rounded-lg border bg-background-default p-5 shadow-sm transition-all duration-200 hover:shadow-md",
        selected && "bg-background-primary-lighter! border-border-active! shadow-none!",
      )}
    >
      <div className="flex items-center justify-between gap-3 font-semibold">
        <span>{getBaggageLabel(baggage)}</span>
        <span className="border-border-minimal flex h-5 w-5 shrink-0 items-center justify-center rounded-full border">
          {selected && <CheckSuccessIcon className="h-full w-full text-icon-success" />}
        </span>
      </div>
      <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
        <div className={classNames("text-font-active flex w-full flex-1 gap-1 text-base font-semibold")}>
          <span>{priceTag}</span>
        </div>
      </div>
    </div>
  );
};

export default function BaggageSelection() {
  const { cursor, flightOptions, updateSSRItem, passengerOptions } = useSSRContext();
  const { journeyDetails } = useJourneyStaticDetails();
  const isLargeScreen = Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const { data } = explode(journeyDetails, {});
  const flightSelected = flightOptions.find((option) => option.value === cursor.flightId);
  const baggageOptions = generateBaggageOptions(flightSelected, data?.ssr?.baggage);
  const handleMealChange = (code: string, value: BaggageOption) => {
    updateSSRItem("baggages", Object.keys(value).length ? { [code]: value } : {});
  };
  const passenger = passengerOptions?.find((pass) => pass?.value === cursor?.passengerId);

  const isEmpty = !baggageOptions?.length;
  if (isEmpty)
    return (
      <div className="flex items-center justify-center">
        <Empty />
      </div>
    );
  return (
    <div className="flex flex-col gap-5 rounded-md bg-background-default p-4">
      {isLargeScreen && <PassengerSelectionTile passenger={passenger} isActive={true} />}{" "}
      <div className="grid w-full flex-1 auto-rows-fr gap-4 md:grid-cols-1 lg:grid-cols-2 lg:gap-5">
        {baggageOptions?.map((baggage) => (
          <div key={`${baggage.code}-${cursor.passengerId}-${flightSelected?.value}`} className="flex items-center">
            <BaggageItem baggage={baggage} onChange={(value: BaggageOption) => handleMealChange(baggage.code, value)} />
          </div>
        ))}
      </div>
    </div>
  );
}
